/// Heavy-weather material — Coastal Skipper / Yachtmaster Offshore +
/// Ocean syllabus. Five domains:
///
///   1. Storm tactics    — heaving-to, lying ahull, fore-reaching,
///                         running off, drogue, sea anchor
///   2. Storm sails      — storm jib + trysail
///   3. Vessel stability — positive vs negative, AVS, GZ curve
///   4. Reefing bands    — wind speed → sail-area state for a typical
///                         35–40 ft cruising yacht
///   5. TRS              — tropical revolving storms: dangerous vs
///                         navigable semicircle, avoidance rules

// ───────────────────────────────────────────────────────────────────
// 1. Storm tactics
// ───────────────────────────────────────────────────────────────────

export type StormTacticId =
  | "heaving-to"
  | "lying-ahull"
  | "fore-reaching"
  | "running-off"
  | "drogue"
  | "sea-anchor";

export interface StormTactic {
  id: StormTacticId;
  name: string;
  /// One-line gist.
  summary: string;
  /// Wind + sea state where this tactic is the right choice.
  whenToUse: string;
  /// Step-by-step setup.
  steps: readonly string[];
  /// What the boat is doing while in this tactic.
  behaviour: string;
  /// Drawbacks + failure modes.
  pitfalls: readonly string[];
}

export const STORM_TACTICS: readonly StormTactic[] = Object.freeze([
  {
    id: "heaving-to",
    name: "Heaving-to (yacht)",
    summary:
      "Tack without re-trimming the jib; lash helm to leeward. Boat stalls at ~50° off the wind, drifting slowly downwind.",
    whenToUse:
      "Up to F8 / 9 in the open ocean. Excellent for a yacht with a hank-on or removable working jib + reefed main. Less effective with a deeply-reefed roller-furling genoa.",
    steps: [
      "Tack the boat WITHOUT releasing the working jib sheet — the jib stays sheeted to the (now-windward) side.",
      "Ease the mainsheet right out so the main flogs gently or is sheeted in tight depending on conditions.",
      "Lash the tiller (or fix the wheel) to leeward.",
      "Boat settles head-up by jib, falls off by rudder — opposing forces balance.",
      "Drift typically 1–2 kt downwind + downwave; slick to windward damps breaking seas.",
    ],
    behaviour:
      "Lying ~50° off the wind, fore-reaching gently, leaving a windward slick that knocks down breaking seas. Crew can rest, eat, fix problems.",
    pitfalls: [
      "Heavy seas + a heavily-reefed main: many yachts heave-to better under storm jib alone or with a trysail.",
      "Modern fin-keel yachts with sleek hulls sometimes won't sit still — fall off and gather way. Try other tactics if heaving-to doesn't settle.",
      "Lee shore in proximity — drift accumulates downwind at 1–2 kt; needs sea room.",
    ],
  },
  {
    id: "lying-ahull",
    name: "Lying ahull",
    summary:
      "Strike all sail, lash helm down, let boat take the weather beam-on. Quickest tactic when overwhelmed but most exposed to breaking seas.",
    whenToUse:
      "Rarely the right choice for offshore yachts — exposes the beam to breaking waves with no manoeuvrability. Acceptable in very limited circumstances when no other tactic is possible.",
    steps: [
      "Drop all sails completely.",
      "Lash tiller / fix wheel — usually down (to leeward).",
      "Secure everything below.",
      "Close all hatches.",
      "Boat drifts downwind at ~2–3 kt, beam-on to wind + seas.",
    ],
    behaviour:
      "Boat lies beam-on, rolls heavily, drifts downwind. NO active steering. Vulnerable to breaking seas.",
    pitfalls: [
      "Modern yachts with high freeboard + light displacement often capsize lying ahull in heavy seas — a breaking wave on the beam can roll the boat.",
      "Generally REPLACED by running off with a drogue or sea-anchor deployment in current heavy-weather doctrine.",
      "Use only as a temporary measure while you set up something better (drogue, sea anchor, fore-reach under storm jib).",
    ],
  },
  {
    id: "fore-reaching",
    name: "Fore-reaching",
    summary:
      "Keep minimum forward way under deeply-reefed main / storm trysail. Helms-on, sails balanced, makes ~1–2 kt of progress to weather of straight downwind drift.",
    whenToUse:
      "When you have sea room + need to slowly make ground to windward or stay clear of a lee shore. The active alternative to heaving-to when sea state is too violent.",
    steps: [
      "Sails: storm trysail or 3rd-reef main + storm jib.",
      "Helmsman keeps the bow close to the wind (~45° off) — high enough to hold ground to weather, low enough to keep the boat moving.",
      "Steer through the worst waves, presenting the bow at ~30° to oncoming crests.",
      "Watches kept tight — helms-on at all times.",
    ],
    behaviour:
      "Boat works to windward at ~1–2 kt over the ground, much less than her usual upwind speed. Actively dodges biggest seas.",
    pitfalls: [
      "Crew fatigue is the limit — fore-reaching needs continuous helm. Plan rotations.",
      "Deeply-reefed main alone (no trysail) flogs in heavy gusts. Trysail much better.",
      "Heavy spray exhausts the helmsman fast. Rotate every 30–60 min.",
    ],
  },
  {
    id: "running-off",
    name: "Running off (bare poles or trailed warps)",
    summary:
      "Run downwind, possibly under bare poles, possibly with warps streamed astern to slow the boat + reduce yawing.",
    whenToUse:
      "Sea room downwind. The tactic when waves are dangerous but you can present the stern to them at controlled speed.",
    steps: [
      "Drop or deeply reef sails. Storm jib OR bare poles depending on wind strength.",
      "Steer dead downwind or 20° off to one side.",
      "Trail warps from the stern in a long bight — typically 30+ m of warp + spare anchor on the bight.",
      "Helmsman steers actively to prevent broaching as each wave passes under the stern.",
      "Boat speed limited by drag of warps to ~4–6 kt — slow enough to be controllable, fast enough to maintain steerage.",
    ],
    behaviour:
      "Boat presents stern to seas + runs ahead of them. Trailed warps slow boat + dampen yaw. Helmsman keeps stern to waves.",
    pitfalls: [
      "Broaching — yawing into the trough at speed, capsize risk. Trailed warps reduce this but constant helmsmanship needed.",
      "Companionway must be closed + secured — pooping wave (sea breaking over the stern) downflows into the cabin if not.",
      "Downwind track may lead toward a lee shore — needs deep sea room.",
    ],
  },
  {
    id: "drogue",
    name: "Drogue (Jordan Series + parachute drogue)",
    summary:
      "Cone-shaped or series-of-discs drag device streamed from the STERN, slowing the boat + holding her bow to seas while running off.",
    whenToUse:
      "Survival-grade running-off. When trailed warps don't slow you enough or yaw is too large. Jordan Series Drogue is the modern offshore-yacht standard.",
    steps: [
      "Deploy from the stern — main bridle attached to TWO strong points (chainplate level), not single cleats.",
      "Pay out gradually — let the drogue fill and start to drag.",
      "Boat slows + stabilises. Speed typically 1.5–3 kt regardless of wind.",
      "Helm to leeward, but boat largely self-steers with the drogue's drag.",
      "Companionway sealed, washboards in place — boat takes occasional waves on the stern that wash forward.",
    ],
    behaviour:
      "Boat sits stern-to-seas, drifting downwind very slowly + with reduced yaw. Crew can rest below.",
    pitfalls: [
      "Bridle attachment points MUST be properly engineered — many yachts have inadequate stern fittings for the loads.",
      "Drogue must be sized for the boat — wrong size = either no slowdown (too small) or risk of being pooped (too much).",
      "Recovery is hard — once the storm passes you need to retrieve the drogue against the residual sea.",
    ],
  },
  {
    id: "sea-anchor",
    name: "Sea anchor (parachute, bow-deployed)",
    summary:
      "Parachute deployed from the BOW, holding the boat head-to-seas + nearly stationary. The textbook 'park and wait it out' tactic.",
    whenToUse:
      "Long-fetch storms where you want to maintain position + present the bow to seas. Multihulls + some monohulls find this their best tactic.",
    steps: [
      "Stream the parachute on a long rode (5–10× boat length) from the BOW — preferably via a bridle off both bow cleats.",
      "Pay out until parachute opens and the boat swings head-to-wind.",
      "Boat now stationary (or drifting slowly downwind at ~0.5 kt).",
      "All sails struck. Helm centred + lashed.",
      "Snubbers + chafe gear on the rode — chafe through the rode is the dominant failure mode.",
    ],
    behaviour:
      "Boat head-to-wind, nearly stationary, riding seas on the bow. Reduced motion + minimal downwind drift.",
    pitfalls: [
      "Chafe through the rode — DIAGNOSED before deployment + chafe gear (anti-chafe sleeves) at the bow roller. Frequent inspection.",
      "Boat may yaw heavily, swinging side-to-side as the parachute opens + collapses. Bridle reduces this.",
      "Monohull yachts with fin keels may sail forward + into the parachute (overrun). Multihulls and full-keel boats handle this better.",
      "Recovery: collapsing the parachute requires running over it or hauling the trip line; non-trivial in residual sea.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Storm sails
// ───────────────────────────────────────────────────────────────────

export interface StormSail {
  id: "storm-jib" | "trysail";
  name: string;
  summary: string;
  /// Where + how it's set.
  rigging: string;
  /// Sail area + typical use range.
  area: string;
  /// When you'd switch to it from a reefed working sail.
  whenToSet: string;
  /// Common mistakes.
  pitfalls: readonly string[];
}

export const STORM_SAILS: readonly StormSail[] = Object.freeze([
  {
    id: "storm-jib",
    name: "Storm jib",
    summary:
      "Small heavy-cloth headsail (~5 m² for a 40-footer) sized for F8+ — strong enough to hold + small enough not to overpower.",
    rigging:
      "Hanked on to a removable inner forestay (preferred) OR set with strops over a furled genoa as a temporary measure. Sheet to a forward-mounted padeye on the toerail — NOT to the genoa car (loads are too high in line with the genoa lead).",
    area:
      "~5–6% of the working-sail-area as a rule of thumb. For a 40-ft cruiser, around 5 m².",
    whenToSet:
      "When working roller genoa is reefed to ~30% and wind is still building. Better to switch to storm jib than to reef the genoa further (heavily-furled genoas set badly, fly poorly + load the furler badly).",
    pitfalls: [
      "Many cruisers don't have an inner forestay — storm jib set on the foredeck via strops works but is awkward to set in heavy weather. Practise rigging it in port.",
      "Storm jib must be in a marked + accessible bag, NOT buried at the bottom of a sail locker. You won't go digging at the moment you need it.",
      "Sheeting position — incorrect lead angle makes the storm jib useless. Mark the padeye + practise the lead.",
    ],
  },
  {
    id: "trysail",
    name: "Storm trysail",
    summary:
      "Heavy-cloth triangular sail set instead of the main when reefed-main is overpowered. Loose-footed, sheet to either side, no boom.",
    rigging:
      "Hoisted on a dedicated mast track parallel to the main track (cruising yachts) or on a separate track + halyard. Boom is dropped + lashed; trysail sheets to the leeward toerail.",
    area:
      "~17.5% of working mainsail area is the ORC formula. For a 40-ft cruiser, around 6–7 m².",
    whenToSet:
      "When 3rd-reef main is overpowered OR the boom is unsafe (broken kicker, broken gooseneck, fear of breakage in continued heavy weather). Trysail removes boom hazard entirely.",
    pitfalls: [
      "Dedicated mast track must be already installed — retrofitting in port is a multi-day job. Plan ahead.",
      "Hoist + sheet trim must be rehearsed in benign conditions — first hoist in F8+ is a recipe for disaster.",
      "Trysail without a track works set hoisted on main track, but only if the main is fully off + stowed — practise.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Vessel stability — positive, neutral, negative, AVS, GZ curve
// ───────────────────────────────────────────────────────────────────

export type StabilityState =
  | "positive"
  | "neutral"
  | "negative"
  | "inverted-stable";

export interface StabilityConcept {
  id: StabilityState;
  name: string;
  /// Heel angle range this applies to.
  heelRange: string;
  /// What's happening physically.
  description: string;
  /// What it means for the crew.
  crewExperience: string;
}

export const STABILITY_CONCEPTS: readonly StabilityConcept[] = Object.freeze([
  {
    id: "positive",
    name: "Positive stability",
    heelRange: "0° up to AVS (typically 120–140° for cruising yachts)",
    description:
      "The metacentre is above the centre of gravity — any heeling moment produces a righting arm GZ that rotates the boat back upright. The 'self-righting' regime.",
    crewExperience:
      "Boat heels, you let off the sheets, boat sits upright again. Normal sailing. Wider angle = more righting force up to a peak around 50–70°.",
  },
  {
    id: "neutral",
    name: "Neutral stability (at AVS)",
    heelRange: "Exactly at AVS — the Angle of Vanishing Stability",
    description:
      "At AVS the centres of buoyancy + gravity align — GZ = 0 — boat has no preference for either rolling back upright or continuing over.",
    crewExperience:
      "The 'tipping point'. Most cruising yachts have AVS ≥ 120°; racing yachts higher. Below the AVS the boat self-rights; beyond it, it stays inverted.",
  },
  {
    id: "negative",
    name: "Negative stability",
    heelRange: "Above AVS up to ~180°",
    description:
      "Centre of gravity is now above the centre of buoyancy — any heel beyond AVS produces a CAPSIZING moment, not a righting one. Boat rolls toward inversion.",
    crewExperience:
      "Once past AVS the boat continues over to inversion — you need a wave to roll the boat back through AVS for it to right itself. Unrigged sometimes does this, rigged usually doesn't.",
  },
  {
    id: "inverted-stable",
    name: "Inverted-stable",
    heelRange: "180° — upside down",
    description:
      "Hull inverted, mast pointing down. This is a SECOND equilibrium for monohulls — they're stable upside down too, sitting in this attitude indefinitely until a wave applies enough energy to roll them back.",
    crewExperience:
      "The catastrophic state. Crew below water (or in air pocket). Recovery requires the boat to be rolled through AVS by a wave + righting moment of the keel (which is now an inverted lever, working AGAINST righting).",
  },
]);

export interface GzCurvePoint {
  /// Heel angle in degrees.
  heelDeg: number;
  /// Righting arm GZ in metres — positive = righting, negative = capsizing.
  /// Representative numbers for a 40-ft cruising yacht with AVS ≈ 125°.
  gzMetres: number;
}

/// Sample GZ curve for a typical 40-ft cruising yacht. Used by the
/// stability diagram in the apps/web Reference panel.
export const SAMPLE_GZ_CURVE: readonly GzCurvePoint[] = Object.freeze([
  { heelDeg: 0, gzMetres: 0 },
  { heelDeg: 10, gzMetres: 0.08 },
  { heelDeg: 20, gzMetres: 0.17 },
  { heelDeg: 30, gzMetres: 0.27 },
  { heelDeg: 40, gzMetres: 0.36 },
  { heelDeg: 50, gzMetres: 0.43 },
  { heelDeg: 60, gzMetres: 0.47 },
  { heelDeg: 70, gzMetres: 0.47 },
  { heelDeg: 80, gzMetres: 0.43 },
  { heelDeg: 90, gzMetres: 0.36 },
  { heelDeg: 100, gzMetres: 0.27 },
  { heelDeg: 110, gzMetres: 0.14 },
  { heelDeg: 120, gzMetres: 0.03 },
  { heelDeg: 125, gzMetres: 0.0 },
  { heelDeg: 130, gzMetres: -0.07 },
  { heelDeg: 140, gzMetres: -0.16 },
  { heelDeg: 150, gzMetres: -0.22 },
  { heelDeg: 160, gzMetres: -0.24 },
  { heelDeg: 170, gzMetres: -0.21 },
  { heelDeg: 180, gzMetres: -0.15 },
]);

/// Angle of Vanishing Stability for the sample yacht.
export const SAMPLE_AVS_DEG = 125;

// ───────────────────────────────────────────────────────────────────
// 4. Reefing bands
// ───────────────────────────────────────────────────────────────────

export type ReefState =
  | "full-sail"
  | "1st-reef"
  | "2nd-reef"
  | "3rd-reef"
  | "trysail-storm-jib"
  | "bare-poles";

export interface ReefingBand {
  state: ReefState;
  /// Wind speed range (in knots) where this state is appropriate for
  /// a typical 35–40 ft cruising yacht.
  windKn: string;
  /// Beaufort force range.
  beaufort: string;
  /// Sail configuration.
  configuration: string;
  /// Crew action / decision point as wind builds.
  decision: string;
}

export const REEFING_BANDS: readonly ReefingBand[] = Object.freeze([
  {
    state: "full-sail",
    windKn: "0 – 14 kt",
    beaufort: "F1 – F4",
    configuration: "Full main + full working genoa / jib.",
    decision:
      "Settled cruising. No reef needed; trim for telltales.",
  },
  {
    state: "1st-reef",
    windKn: "14 – 21 kt",
    beaufort: "F4 – F5",
    configuration: "1st reef in the main + roller genoa to ~85%.",
    decision:
      "First reef when you THINK about reefing. Boat over-pressed if heel > 25° or weather helm > 5°. Reef early — it's faster to shake out than to put in.",
  },
  {
    state: "2nd-reef",
    windKn: "21 – 28 kt",
    beaufort: "F5 – F6",
    configuration: "2nd reef in the main + roller genoa to ~60%.",
    decision:
      "Second reef once the first is no longer enough. Crew comfort declines markedly above F5 — reef preserves morale + boat speed.",
  },
  {
    state: "3rd-reef",
    windKn: "28 – 38 kt",
    beaufort: "F6 – F8",
    configuration: "3rd reef in main + storm jib OR ~35% genoa.",
    decision:
      "Heavy-weather configuration. Crew in lifejackets + harnesses + clipped on. Watch system tightened. If wind continues to build, plan to switch to trysail.",
  },
  {
    state: "trysail-storm-jib",
    windKn: "38 – 55 kt",
    beaufort: "F8 – F10",
    configuration: "Storm trysail + storm jib (or just storm jib).",
    decision:
      "Survival sail plan. Boat fore-reaches or heaves-to. All crew below except the watch. Storm tactics (drogue / sea anchor) under consideration.",
  },
  {
    state: "bare-poles",
    windKn: "55+ kt",
    beaufort: "F10+",
    configuration: "All sail down. Running off with warps / drogue or hove-to under sea anchor.",
    decision:
      "Storm tactics in force. Crew rotation tight, EPIRB + grab bag at the companionway. Communicate plan + alternatives.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 5. TRS — Tropical Revolving Storms
// ───────────────────────────────────────────────────────────────────

export interface TrsZone {
  id: "dangerous" | "navigable";
  name: string;
  /// Which side of the storm track the zone is on, by hemisphere.
  position: string;
  /// What's happening with wind direction + waves.
  conditions: string;
  /// What it does to a sailing boat trying to escape.
  effect: string;
  /// Escape strategy.
  escape: string;
}

export const TRS_ZONES: readonly TrsZone[] = Object.freeze([
  {
    id: "dangerous",
    name: "Dangerous semicircle",
    position:
      "Northern hemisphere: RIGHT side of the storm's track (looking along the direction of movement). Southern hemisphere: LEFT side.",
    conditions:
      "Storm's translation speed ADDS to the rotational wind speed — winds 30%+ stronger than the navigable side. Wind direction veers as the storm passes — pushing sailing boats further into the storm.",
    effect:
      "Wind tries to drive you toward the storm's path AHEAD of it. Sailing across the wind keeps you in the dangerous zone longer. Worst place to be.",
    escape:
      "Run off with the wind on the STARBOARD QUARTER (N hem) / PORT QUARTER (S hem) — this opens the angle and gets you behind the storm's track. Don't try to sail across the front of it.",
  },
  {
    id: "navigable",
    name: "Navigable semicircle",
    position:
      "Northern hemisphere: LEFT side of the storm's track. Southern hemisphere: RIGHT side.",
    conditions:
      "Storm's translation SUBTRACTS from rotational wind speed — winds noticeably weaker than the dangerous side. Wind backs (N hem) / veers (S hem) as storm passes — pushes you AWAY from the storm's centre.",
    effect:
      "Wind helps you escape. Sailing with the wind on the bow drives you clear behind the storm's track.",
    escape:
      "Run off with wind on the STARBOARD BOW (N hem) / PORT BOW (S hem) — drives you progressively south + away from the storm in N hem. Goal: get behind the storm.",
  },
]);

/// Quick-reference summary of the TRS avoidance rules. Used as the
/// reveal text on the "which side of the storm?" drill.
export const TRS_RULES: readonly string[] = Object.freeze([
  "Northern hemisphere TRS: track typically moves WNW then recurves NE. Right of track is DANGEROUS, left is NAVIGABLE.",
  "Southern hemisphere TRS: track typically moves WSW then recurves SE. Left of track is DANGEROUS, right is NAVIGABLE.",
  "Buys Ballot's Law tells you which side: face the wind, low pressure is on your left (N hem) / right (S hem). Then compare to the forecast storm track.",
  "Watch for: barometer falling rapidly, swell from a fixed direction independent of local wind, increasing cloud (cirrus → altostratus → cumulonimbus), heavy distant lightning at night.",
  "Avoidance strategy: get at least 100 nm clear of the forecast track + 200 nm clear of the centre. If you must navigate near, stay in the navigable semicircle.",
]);
